import React, { useRef, useState, useCallback } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';

/**
 * BeforeAfterSlider — comparateur avant/après avec curseur glissant (remplace la vue
 * côte-à-côte, demande explicite utilisateur 16/09/2026). Glisser horizontalement pour
 * révéler la photo "Avant" par-dessus la photo "Après".
 */
export const BeforeAfterSlider = ({ beforeUrl, afterUrl, beforeLabel, afterLabel, beforeMeta, afterMeta, en }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(98, Math.max(2, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    updatePos(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    updatePos(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 bg-zinc-800 select-none touch-none cursor-ew-resize"
      onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
      onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
      data-testid="before-after-slider"
    >
      {/* Après (fond, pleine largeur) */}
      {afterUrl
        ? <img src={afterUrl} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover object-[50%_18%]" draggable={false} />
        : <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>}
      <span className="absolute top-2 right-2 z-10 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider bg-gradient-to-r from-primary to-pink-500 text-white">{afterLabel}</span>
      {afterMeta && (
        <span className="absolute bottom-2 right-2 z-10 px-2 py-0.5 rounded-full bg-black/55 text-[10px] font-semibold text-white/90 whitespace-nowrap">{afterMeta}</span>
      )}

      {/* Avant (recouvre partiellement via clip-path, largeur visible = pos%) */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        {beforeUrl
          ? <img src={beforeUrl} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover object-[50%_18%]" draggable={false} />
          : <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>}
        <span className="absolute top-2 left-2 z-10 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider bg-black/70 text-white">{beforeLabel}</span>
        {beforeMeta && (
          <span className="absolute bottom-2 left-2 z-10 px-2 py-0.5 rounded-full bg-black/55 text-[10px] font-semibold text-white/90 whitespace-nowrap">{beforeMeta}</span>
        )}
      </div>

      {/* Curseur */}
      <div className="absolute inset-y-0 z-20 w-0.5 bg-white/80 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center">
          <ArrowLeftRight className="w-4 h-4 text-black" />
        </div>
      </div>

      <p className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 text-[9px] font-semibold text-white/60 whitespace-nowrap pointer-events-none">
        {en ? 'Drag to compare' : 'Glissez pour comparer'}
      </p>
    </div>
  );
};

export default BeforeAfterSlider;
